import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { BarChart3, Mail, Loader2, MapPin } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tableau de bord des montants encaissés par territoire + envoi du récap mensuel.
export function FTPEVirementBilan({ headers }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [bilan, setBilan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const loadBilan = async () => {
    setLoading(true);
    try {
      const params = {};
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const { data } = await axios.get(
        `${API_URL}/api/ftpe-adhesion/admin/virements-bilan`,
        { headers, params }
      );
      setBilan(data);
    } catch {
      toast.error('Erreur lors du chargement du bilan');
    } finally {
      setLoading(false);
    }
  };

  const sendRecap = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/ftpe-adhesion/admin/virements-recap-mensuel/send`,
        {},
        { headers }
      );
      if (data.success) {
        toast.success(`Récap ${data.period} envoyé à ${data.sent_to}`);
      } else {
        toast.warning(data.reason || 'Récap non envoyé');
      }
    } catch {
      toast.error('Erreur lors de l\u2019envoi du récap');
    } finally {
      setSending(false);
    }
  };

  const maxMontant = bilan?.territoires?.reduce((m, r) => Math.max(m, r.montant), 0) || 1;

  return (
    <Card data-testid="virement-bilan-card">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <BarChart3 className="w-5 h-5 text-emerald-600" />
            Bilan par territoire
          </CardTitle>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-col">
              <label className="text-[10px] text-gray-500 mb-0.5">Du</label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-[140px] h-9" data-testid="bilan-date-from" />
            </div>
            <div className="flex flex-col">
              <label className="text-[10px] text-gray-500 mb-0.5">Au</label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-[140px] h-9" data-testid="bilan-date-to" />
            </div>
            <Button onClick={loadBilan} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bilan-load-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Afficher'}
            </Button>
            <Button onClick={sendRecap} variant="outline" disabled={sending} data-testid="bilan-send-recap-btn">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Envoyer le récap
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!bilan ? (
          <p className="text-sm text-gray-500">Choisissez une période (ou laissez vide pour tout) puis « Afficher ».</p>
        ) : bilan.territoires.length === 0 ? (
          <p className="text-sm text-gray-500">Aucun virement validé sur la période.</p>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm font-semibold text-gray-700 border-b pb-2">
              <span>Total encaissé</span>
              <span data-testid="bilan-total">{bilan.total_montant.toFixed(2)} € — {bilan.total_nombre} virement(s)</span>
            </div>
            {bilan.territoires.map((r) => (
              <div key={r.territoire} data-testid={`bilan-row-${r.territoire}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="flex items-center gap-1 font-medium text-gray-800">
                    <MapPin className="w-3.5 h-3.5 text-emerald-500" /> {r.territoire}
                  </span>
                  <span className="text-gray-600">{r.montant.toFixed(2)} € · {r.nombre}</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600"
                    style={{ width: `${Math.max(4, (r.montant / maxMontant) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default FTPEVirementBilan;
